import { useEffect, useMemo } from 'react';
import { assignColumnAccessor, Column, ColumnIdentity } from './useTablePages';
import useUiSettings from './useUiSettings';

export type ColumnConfig = {
  header: string;
  id: string;
  enabled: boolean;
};

const getColumnId = (column: ColumnIdentity) =>
  column.id || (column.accessor as string);

const useColumnConfiguration = (availableColumns: Column[], page: string) => {
  useEffect(() => {
    availableColumns.forEach(assignColumnAccessor);
  }, [availableColumns]);

  const [storedConfig, setStoredConfig, loading] = useUiSettings<
    ColumnConfig[]
  >(page, 'columns');

  const cleaned: ColumnConfig[] = useMemo(() => {
    const cleaned = (storedConfig ?? [])
      .slice()
      // remove columns that are no longer available
      .filter((column) =>
        availableColumns.find(
          (available) => column.id === getColumnId(available)
        )
      )
      .map((column) => {
        const found = availableColumns.find(
          (available) => column.id === getColumnId(available)
        );

        return {
          ...column,
          header: found?.header,
        };
      });

    // add new columns that weren't saved
    availableColumns.forEach((column, index) => {
      const id = getColumnId(column);
      const found = !!cleaned.find((config) => config.id === id);
      if (!found) {
        cleaned.splice(index, 0, {
          id,
          header: column.header,
          enabled: true,
        });
      }
    });

    return cleaned;
  }, [storedConfig, availableColumns]);

  const visibleColumns = useMemo(
    () =>
      cleaned
        .filter((config) => config.enabled)
        .map((config) => {
          return availableColumns.find(
            (column) => getColumnId(column) === config.id
          ) as Column;
        }),
    [cleaned, availableColumns]
  );

  return {
    visibleColumns,
    columnConfiguration: cleaned,
    setColumnConfiguration: setStoredConfig,
    loading,
  };
};

export default useColumnConfiguration;
